import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tv, Eye, Loader2, Square, RefreshCw, Users } from "lucide-react";
import api, { formatErr } from "../lib/api";

const PRESETS = [50, 100, 200, 500, 1000];

function StreamCard({ s, onChanged }) {
  const [count, setCount] = useState(200);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const run = async (stop) => {
    setBusy(true); setMsg("");
    try {
      const { data } = stop
        ? await api.post(`/admin/live/${s.id}/boost-viewers/stop`)
        : await api.post(`/admin/live/${s.id}/boost-viewers`, { count: Number(count) });
      setMsg(stop ? `Removed ${data.removed} demo viewers` : `Adding ${data.adding} demo viewers — they join over the next few seconds`);
      onChanged();
    } catch (e) { setMsg(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3" data-testid={`live-boost-card-${s.id}`}>
      <div className="flex items-center gap-3">
        <img src={s.user?.avatar} alt="" className="w-11 h-11 rounded-full border-2 border-[#FF2A5F]"/>
        <div className="flex-1 min-w-0">
          <Link to={`/u/${s.user?.id}`} className="font-bold truncate block">@{s.user?.username || "streamer"}</Link>
          <div className="text-[11px] text-white/55 truncate">{s.title || "Live"} · {s.category || "general"}</div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1 font-outfit font-extrabold text-lg" data-testid={`live-boost-viewers-${s.id}`}><Eye className="w-4 h-4"/>{s.viewers || 0}</div>
          <div className="text-[10px] text-[#00E5FF] font-bold" data-testid={`live-boost-boosted-${s.id}`}>{s.boosted || 0} boosted</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map(p => (
          <button key={p} type="button" onClick={() => setCount(p)} data-testid={`live-boost-preset-${p}-${s.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold active:scale-95 ${Number(count) === p ? "bg-[#00E5FF] text-black" : "bg-white/10"}`}>{p}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <input type="number" min={1} max={3000} value={count} onChange={e => setCount(e.target.value)} data-testid={`live-boost-count-${s.id}`}
          className="w-28 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF] text-sm"/>
        <button type="button" disabled={busy || !(Number(count) > 0)} onClick={() => run(false)} data-testid={`live-boost-go-${s.id}`}
          className="flex-1 py-2.5 rounded-xl bg-[#00E5FF] text-black font-bold text-sm flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Users className="w-4 h-4"/>}Boost viewers
        </button>
        <button type="button" disabled={busy || !s.boosted} onClick={() => run(true)} data-testid={`live-boost-stop-${s.id}`}
          className="px-3 py-2.5 rounded-xl bg-[#FF2A5F]/15 text-[#FF2A5F] border border-[#FF2A5F]/30 font-bold text-sm flex items-center gap-1 active:scale-95 disabled:opacity-40">
          <Square className="w-3.5 h-3.5"/>Stop
        </button>
      </div>
      {msg && <p className="text-xs text-white/70" data-testid={`live-boost-msg-${s.id}`}>{msg}</p>}
    </div>
  );
}

export default function AdminLiveBoost() {
  const [streams, setStreams] = useState(null);
  const load = useCallback(() => { api.get("/admin/live/streams").then(r => setStreams(r.data)).catch(() => setStreams([])); }, []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  return (
    <div className="space-y-4" data-testid="admin-live-boost">
      <div className="p-4 rounded-2xl bg-gradient-to-br from-[#00E5FF]/10 to-transparent border border-[#00E5FF]/25">
        <div className="flex items-center justify-between">
          <h2 className="font-outfit font-bold text-lg flex items-center gap-2"><Tv className="w-5 h-5 text-[#00E5FF]"/>Boost Live Streamer Viewers</h2>
          <button type="button" onClick={load} data-testid="live-boost-refresh" className="p-2 rounded-full bg-white/10 active:scale-90"><RefreshCw className="w-4 h-4"/></button>
        </div>
        <p className="text-xs text-white/65 mt-1">Pick a live streamer and add demo accounts as viewers. They appear in the viewer list and chat shows “joined”. They stay until the live ends or you press Stop.</p>
      </div>
      {streams === null && <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin"/></div>}
      {streams?.length === 0 && <div className="p-8 text-center text-white/55 text-sm rounded-2xl bg-white/5 border border-white/10" data-testid="live-boost-empty">No one is live right now. This list refreshes every 5 seconds.</div>}
      {streams?.map(s => <StreamCard key={s.id} s={s} onChanged={load}/>)}
    </div>
  );
}
